import { captureException } from '@sentry/browser'
import produce from 'immer'
import { PropertyPath } from 'lodash'
import set from 'lodash/set'

import { fetchUserSettingsSuccess } from 'actions/auth'
import {
    getUserSettings,
    updateUserSettings as updateUserSettingsApi,
    UserSettings,
} from 'api/Cerebro/unscoped/user'
import { date } from 'configuration/dayjs'
import { selectDomainValue as selectAuthDomainValue } from 'selectors/auth'

import { dispatch, getReduxState } from './reduxBridge'

export interface SettingPair {
    path: PropertyPath
    value: any
}

type ShouldCommit = () => boolean

export async function fetchUserSettings(
    shouldCommit?: ShouldCommit
): Promise<UserSettings> {
    let userSettings: UserSettings = selectAuthDomainValue(
        getReduxState(),
        'settings'
    )

    // Cache settings for 1 hour
    if (
        !userSettings ||
        !userSettings.lastUpdated ||
        date(userSettings.lastUpdated).isBefore(date().subtract(1, 'hour'))
    ) {
        userSettings = await getUserSettings()
        if (!shouldCommit || shouldCommit()) {
            dispatch(
                fetchUserSettingsSuccess({
                    ...userSettings,
                    lastUpdated: date(),
                })
            )
        }
    }

    return userSettings
}

export async function updateUserSettings(
    settingPairs: SettingPair[],
    shouldCommit?: ShouldCommit
): Promise<void> {
    if (!settingPairs || settingPairs.length === 0) {
        return
    }

    const originalSettings = await fetchUserSettings(shouldCommit)

    const updatedSettings = produce(originalSettings, (draft) => {
        settingPairs.forEach(({ path, value }) => {
            set(draft, path, value)
        })
    })

    // A superseded org-switch must neither write to Redux nor persist its
    // now-stale settings (e.g. the old active organizationId) to the server.
    if (shouldCommit && !shouldCommit()) {
        return
    }

    dispatch(
        fetchUserSettingsSuccess({
            ...updatedSettings,
            lastUpdated: date(),
        })
    )

    // Fire-and-forget persistence (the saga used `spawn`); isolate its errors.
    void updateUserSettingsApi(updatedSettings).catch(captureException)
}
